/**
 * BUSCAR UNA PERSONA Y ELEGIRLA.
 *
 * POR QUÉ EXISTE. `ReportFriendly` tenía esta búsqueda escrita adentro, y
 * alcanzaba mientras hubiera UNA persona que elegir. En dobles hay TRES —mi
 * pareja, el rival y la pareja del rival— y copiar el bloque tres veces sería
 * copiar también la cancelación de consultas, el mínimo de letras y el manejo
 * del error, que son justo las partes que nadie se acuerda de repetir bien.
 *
 * LA BÚSQUEDA SE CANCELA SOLA. Quien escribe «Ana» manda cuatro consultas y
 * las respuestas pueden llegar en cualquier orden: sin cancelar, la de «An»
 * puede pisar a la de «Ana» y la lista queda mostrando resultados de algo que
 * ya nadie escribió.
 *
 * `exclude` SON LAS PERSONAS QUE YA ESTÁN EN EL PARTIDO. No es cosmética: sin
 * esto, la lista ofrece a quien ya elegiste en otro campo, lo elegís, y el
 * servidor te contesta que no se puede. El error correcto es el que no se ve
 * porque la opción nunca se ofreció.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CHALLENGE_TEXT_MIN, type ChallengePlayer } from '@tennisup/contracts';

import { Avatar } from './Avatar';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function PlayerPicker({
  id,
  label,
  help,
  chosen,
  onChoose,
  exclude = [],
}: {
  id: string;
  label: string;
  help?: string;
  chosen: ChallengePlayer | null;
  onChoose: (player: ChallengePlayer | null) => void;
  exclude?: readonly string[];
}): JSX.Element {
  const { t } = useTranslation();
  const [texto, setTexto] = useState('');
  const [encontrados, setEncontrados] = useState<ChallengePlayer[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const corte = useRef<AbortController | null>(null);

  const buscar = useCallback(
    async (q: string) => {
      corte.current?.abort();
      if (q.trim().length < CHALLENGE_TEXT_MIN) {
        setEncontrados([]);
        setBuscando(false);
        return;
      }
      const mio = new AbortController();
      corte.current = mio;
      setBuscando(true);
      try {
        const filas = await api.searchChallengers(
          { kind: 'FRIENDLY', text: q.trim(), km: null, communityId: null, sameCategory: false },
          mio.signal,
        );
        if (mio.signal.aborted) return;
        // `Array.isArray` y no confiar en el tipo: lo que llega es JSON de la
        // red, y `filas.map` sobre otra cosa revienta la pantalla entera.
        setEncontrados(Array.isArray(filas) ? filas : []);
      } catch (caught) {
        if (mio.signal.aborted) return;
        setError(errorMessage(t, caught));
        setEncontrados([]);
      } finally {
        if (!mio.signal.aborted) setBuscando(false);
      }
    },
    [t],
  );

  useEffect(() => {
    const reloj = setTimeout(() => void buscar(texto), 250);
    return () => clearTimeout(reloj);
  }, [texto, buscar]);

  useEffect(() => () => corte.current?.abort(), []);

  if (chosen !== null) {
    return (
      <p className="friendly-chosen">
        <Avatar url={assetUrl(chosen.avatarPath)} name={chosen.displayName} size={32} />
        <strong>{chosen.displayName}</strong>
        <button type="button" className="link" onClick={() => onChoose(null)}>
          {t('friendly.change')}
        </button>
      </p>
    );
  }

  const ofrecidos = encontrados.filter((p) => !exclude.includes(p.userId));

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="search"
        value={texto}
        placeholder={t('friendly.whoPlaceholder')}
        onChange={(e) => setTexto(e.target.value)}
      />
      {help !== undefined && <span className="help">{help}</span>}

      {error !== null && <span className="error">{error}</span>}
      {buscando && <p className="help">{t('common.loading')}</p>}

      {!buscando && texto.trim().length >= CHALLENGE_TEXT_MIN && ofrecidos.length === 0 && (
        <p className="help">{t('friendly.noneFound')}</p>
      )}

      {ofrecidos.length > 0 && (
        <ul className="friendly-results">
          {ofrecidos.map((p) => (
            <li key={p.userId}>
              <button type="button" className="friendly-pick" onClick={() => onChoose(p)}>
                <Avatar url={assetUrl(p.avatarPath)} name={p.displayName} size={32} />
                <span className="friendly-name">
                  {p.displayName}
                  {p.locationLabel !== null && p.locationLabel !== '' && (
                    <small>{p.locationLabel}</small>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
